// ===============================================================
// File Library — Dashboard
// Stats overview, recent documents, expiring soon alerts
// ===============================================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Table, Center,
  Loader, ThemeIcon,
} from '../../components/gab/GabComponents';
import {
  IconFiles, IconUpload, IconAlertTriangle, IconClockX,
  IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls, IconFileTypePpt,
  IconPhoto, IconFile,
} from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLCategory } from '../../store/fileLibraryStore';
import { semantic } from '../../lib/colors';

function getCatColor(catIdOrName: string, categories: FLCategory[]): string {
  const cat = categories.find(c => String(c.id) === catIdOrName || c.name === catIdOrName);
  return cat?.color || '#7B7D7F';
}

function getFileIcon(fileType: string) {
  const t = (fileType || '').toLowerCase();
  if (t === 'pdf') return IconFileTypePdf;
  if (t === 'doc' || t === 'docx') return IconFileTypeDoc;
  if (t === 'xls' || t === 'xlsx') return IconFileTypeXls;
  if (t === 'ppt' || t === 'pptx') return IconFileTypePpt;
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(t)) return IconPhoto;
  return IconFile;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { documents, categories, loading, loadDocuments, loadCategories } = useFileLibraryStore();

  useEffect(() => {
    loadDocuments();
    loadCategories();
  }, []);

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const totalDocs = documents.length;

  const publishedThisMonth = documents.filter(d => {
    if (d.status !== 'published' || !d.publishDate) return false;
    const pd = new Date(d.publishDate);
    return pd.getMonth() === currentMonth && pd.getFullYear() === currentYear;
  }).length;

  const expiringSoon = documents.filter(d => {
    if (!d.expirationDate) return false;
    const exp = new Date(d.expirationDate);
    return exp > now && exp <= thirtyDaysFromNow;
  });

  const expiredDocs = documents.filter(d => {
    if (!d.expirationDate) return false;
    return new Date(d.expirationDate) < now;
  });

  const recentPublished = documents
    .filter(d => d.status === 'published')
    .sort((a, b) => (b.publishDate ?? '').localeCompare(a.publishDate ?? ''))
    .slice(0, 6);

  const expiringSorted = [...expiringSoon].sort((a, b) =>
    (a.expirationDate ?? '').localeCompare(b.expirationDate ?? '')
  );

  // Build category ID → name lookup
  const catNameMap: Record<string, string> = {};
  categories.forEach(c => { catNameMap[String(c.id)] = c.name; });
  const getCatName = (id: string) => catNameMap[id] || (/^\d+$/.test(id) ? 'Uncategorized' : (id || 'Uncategorized'));

  // Category counts (keyed by display name, not ID)
  const categoryCounts: Record<string, number> = {};
  documents.forEach(d => {
    if (d.category) {
      const name = getCatName(d.category);
      categoryCounts[name] = (categoryCounts[name] || 0) + 1;
    }
  });

  return (
    <div>
      <Title order={3} mb="md" c="black">File Library Dashboard</Title>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconFiles size={20} color="#7B7D7F" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700}>{totalDocs}</Text>
          <Text size="xs" c="dimmed">Total Documents</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconUpload size={20} color="#2563eb" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c="#2563eb">{publishedThisMonth}</Text>
          <Text size="xs" c="dimmed">Published This Month</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconAlertTriangle size={20} color="#A67103" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c="#A67103">{expiringSoon.length}</Text>
          <Text size="xs" c="dimmed">Expiring Soon</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <IconClockX size={20} color={semantic.error} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.error}>{expiredDocs.length}</Text>
          <Text size="xs" c="dimmed">Expired</Text>
        </Paper>
      </SimpleGrid>

      {/* Recently Published */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Text fw={600} size="sm" mb="sm">Recently Published</Text>
        {recentPublished.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">No published documents yet.</Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            {recentPublished.map(doc => {
              const FileIcon = getFileIcon(doc.fileType ?? '');
              return (
                <Paper key={doc.id} p="sm" radius="sm" withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/filelibrary/doc/${doc.id}`)}
                >
                  <Group gap="xs" mb={4}>
                    <ThemeIcon size={24} variant="light" color="#939598">
                      <FileIcon size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={600} style={{ flex: 1 }} lineClamp={1}>{doc.title}</Text>
                  </Group>
                  <Badge size="xs" mb={4} style={{ backgroundColor: getCatColor(doc.category ?? '', categories), color: '#fff' }}>
                    {getCatName(doc.category ?? '')}
                  </Badge>
                  <Text size="xs" c="dimmed" lineClamp={2} mb={4}>{doc.description || 'No description'}</Text>
                  <Text size="xs" c="dimmed">
                    Published {doc.publishDate ? new Date(doc.publishDate).toLocaleDateString() : '—'}
                  </Text>
                </Paper>
              );
            })}
          </SimpleGrid>
        )}
      </Paper>

      {/* Expiring Within 30 Days */}
      {expiringSorted.length > 0 && (
        <Paper p="md" radius="sm" withBorder mb="md" style={{ border: '1px solid #A67103' }}>
          <Group gap="xs" mb="sm">
            <IconAlertTriangle size={16} color="#A67103" />
            <Text fw={600} size="sm" c="#A67103">Expiring Within 30 Days</Text>
          </Group>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expiringSorted.map(doc => (
                <Table.Tr key={doc.id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/filelibrary/doc/${doc.id}`)}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{doc.title}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" style={{ backgroundColor: getCatColor(doc.category ?? '', categories), color: '#fff' }}>
                      {getCatName(doc.category ?? '')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{doc.expirationDate ? new Date(doc.expirationDate).toLocaleDateString() : '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={doc.priority === 'critical' ? 'var(--color-error)' : doc.priority === 'important' ? '#F86A0B' : '#939598'}>
                      {doc.priority || 'normal'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color="var(--color-success)" variant="light">{doc.status}</Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* By Category */}
      <Paper p="md" radius="sm" withBorder>
        <Text fw={600} size="sm" mb="sm">By Category</Text>
        {Object.keys(categoryCounts).length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">No categories yet.</Text>
        ) : (
          <Group gap="xs">
            {Object.entries(categoryCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([catName, count]) => (
                <Badge
                  key={catName}
                  size="lg"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: getCatColor(catName, categories),
                    color: '#fff',
                  }}
                  onClick={() => navigate(`/filelibrary/browse?category=${encodeURIComponent(catName)}`)}
                >
                  {catName} ({count})
                </Badge>
              ))}
          </Group>
        )}
      </Paper>
    </div>
  );
}
